import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, MagicWand, Megaphone } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Input } from "@/components/ui/input";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { EmptyState } from "../components/EmptyState";
import { CardsSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { CampaignCard } from "../components/campaign/CampaignCard";

export default function Campaigns() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    if (!currentOrg) return;
    let alive = true;
    setLoading(true);
    api.get("/promotions").then((r) => alive && setPromotions(r.data || []))
      .catch(() => alive && setPromotions([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [currentOrg]);

  const filtered = useMemo(() => {
    return promotions.filter((p) =>
      (status === "all" || p.status === status) &&
      (!q || p.name?.toLowerCase().includes(q.toLowerCase())),
    );
  }, [promotions, q, status]);

  const statusOptions = ["all", "draft", "active", "paused", "completed"].map((s) => ({
    value: s, label: s === "all" ? t("promo.campaigns.filterAll") : t(`promo.campaigns.status.${s}`),
  }));

  return (
    <div className="space-y-8" data-testid="campaigns-page">
      <PromoPageHeader
        overline={t("promo.nav.campaigns")}
        title={t("promo.campaigns.title")}
        description={t("promo.campaigns.subtitle")}
        actions={
          <Link to="/promotions/new"
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            data-testid="campaigns-new-btn">
            <MagicWand size={16} weight="fill" /> {t("promo.nav.new")}
          </Link>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("promo.campaigns.search")}
            className="bg-card pl-9" data-testid="campaigns-search" />
        </div>
        <div className="w-full sm:w-52">
          <PromoSelect value={status} onChange={setStatus} options={statusOptions} testId="campaigns-status-filter" />
        </div>
      </div>

      {loading ? (
        <CardsSkeleton count={6} mediaHeight="h-16" />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={Megaphone} title={t("promo.campaigns.emptyTitle")} description={t("promo.campaigns.emptyDesc")}
          action={
            <Link to="/promotions/new" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="campaigns-empty-new">
              {t("promo.nav.new")}
            </Link>
          } />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3" data-testid="campaigns-grid">
          {filtered.map((p, i) => <CampaignCard key={p.id} promo={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
